import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  PlusOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  UserOutlined,
  IdcardOutlined
} from '@ant-design/icons'
import { Space, Spin } from 'antd'
import debounce from 'lodash.debounce'

import { PageContainer, Button, Input } from 'ui/styled'

import Header from 'components/Header'
import MemberSelect from 'components/MemberSelect'
import FilterSystem from 'components/FilterSystem'
import FacilitiesFilters from 'components/FacilitiesFilters'

import LocationsAutocomplete from 'components/LocationsAutocomplete'
import Map from 'components/Map'
import { FACILITIES_PATH } from 'constants/routes'

import {
  getFromStorage,
  removeFromStorage,
  setToStorage
} from 'utils/localStorage'
import { isNilOrEmpty } from 'utils/helpers'

import {
  Root,
  SearchSection,
  ResultsSection,
  ResultsTable,
  ResultsMap,
  IconContainer
} from 'ui/entities/styled'

import InsuranceAutocomplete from 'components/InsuranceAutocomplete'
import { useCarePlanDraftStore } from 'stores/carePlanDraft'
import CarePlanDraftSider from 'pages/CarePlanDraft/components/Sider'
import FacilitiesTable from './FacilitiesTable'
import FacilityCreateModal from './FacilityCreateModal'

const FILTERS_STORAGE_NAME = 'filters_facilities_page'
const DEFAULT_FILTERS = { distance: 20 }

const Facilities = ({
  onSelectEntity = null,
  onRemoveEntity = null,
  entitiesIDs = [],
  isStandalonePage = false,
  isSelectionMode = false,
  addButtonText = false,
  memberId = false,
  careStepFilters,
  resetCareStepFilters
}) => {
  const { carePlanDraft } = useFlags()
  const [searchParams] = useSearchParams()

  const [filtersResetKey, setFiltersResetKey] = useState(Date.now())
  const [markers, setMarkers] = useState()
  const [markersLoading, setMarkersLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [planTiers, setPlanTiers] = useState()

  const activeDraftId = useCarePlanDraftStore(s => s.activeDraftId)
  const isCarePlanDraftCreation =
    carePlanDraft && isStandalonePage && activeDraftId

  const getInitialFilters = () => {
    if (careStepFilters) return careStepFilters
    if (!isStandalonePage) return DEFAULT_FILTERS

    if (searchParams.size) {
      const initialFilters = {
        ...DEFAULT_FILTERS,
        ...Object.fromEntries(searchParams)
      }

      setToStorage(FILTERS_STORAGE_NAME)(initialFilters)
      return initialFilters
    }

    return getFromStorage(FILTERS_STORAGE_NAME)() || DEFAULT_FILTERS
  }

  const filtersSaved = getInitialFilters()

  const [insuranceModalState, setInsuranceModalState] = useState({
    visible: false
  })

  const handleEntitySelect = useCallback(
    entity => {
      if (onSelectEntity) return onSelectEntity(entity)
      if (isCarePlanDraftCreation) {
        return setInsuranceModalState({
          visible: true,
          data: {
            entity,
            id: entity.id,
            name: entity.name,
            avatarSrc: entity.image_url,
            isFacility: true,
            hideModal: () => setInsuranceModalState({ visible: false })
          }
        })
      }
    },
    [isCarePlanDraftCreation, onSelectEntity]
  )

  const onModalOpen = () => setIsCreateModalOpen(true)
  const onModalClose = () => setIsCreateModalOpen(false)

  const filterSystemRenderKey = isNilOrEmpty(activeDraftId)
    ? 'filter-system'
    : `filter-system-${activeDraftId}`

  return (
    <div>
      {isStandalonePage && (
        <>
          <Helmet>
            <title>Facilities | Rightway Healthcare</title>
          </Helmet>
          <Header
            title="Facilities"
            buttons={[
              <Button
                onClick={onModalOpen}
                btntype="primary"
                btnsize="custom"
                padding="8px 12px"
                data-test="facilitiesTabAddNewFacilitiesBtn"
              >
                <PlusOutlined />
                Add New Facility
              </Button>
            ]}
            isDraftsMenuVisible
            isArrowHidden
          />
        </>
      )}

      <FilterSystem key={filterSystemRenderKey} defaults={filtersSaved}>
        {(
          filterValues,
          updateFilters,
          updateSeveralFilters,
          setFilters,
          resetFilters
        ) => {
          const filtersUpdate = (name, value) => {
            const res = updateFilters(name, value)
            if (isStandalonePage) setToStorage(FILTERS_STORAGE_NAME)(res)
          }

          const onAddressSelect = v => {
            filtersUpdate('address', v)
          }

          const debouncedFiltersUpdate = debounce(filtersUpdate, 500)
          const debouncedAddressSelect = debounce(onAddressSelect, 500)

          const filtersUpdateSeveral = val => {
            const res = updateSeveralFilters(val)
            if (isStandalonePage) setToStorage(FILTERS_STORAGE_NAME)(res)
          }

          const filtersReset = () => {
            if (careStepFilters) resetCareStepFilters()
            if (isStandalonePage || careStepFilters)
              removeFromStorage(FILTERS_STORAGE_NAME)
            resetFilters({ distance: 20 })
          }

          const onResetFilters = () => {
            setFiltersResetKey(Date.now())
            filtersReset()
          }

          return (
            <Root>
              <PageContainer
                width="100%"
                padding="20px"
                margin={!isStandalonePage && '0'}
                style={
                  !isStandalonePage ? { height: 'calc(100vh - 340px)' } : {}
                }
              >
                <SearchSection key={filtersResetKey}>
                  <Space
                    orientation="vertical"
                    size="middle"
                    style={{ width: '100%' }}
                  >
                    <Space.Compact size="large" block>
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder="Facility Name"
                        onChange={e =>
                          debouncedFiltersUpdate('name', e.target.value)
                        }
                        defaultValue={filterValues.name}
                        data-test="facilitiesPageSearch"
                        fontSize={16}
                      />
                      <IconContainer>
                        <EnvironmentOutlined />
                      </IconContainer>
                      <LocationsAutocomplete
                        memberId={
                          isStandalonePage ? filterValues.member_id : memberId
                        }
                        onSelect={onAddressSelect}
                        onSearch={debouncedAddressSelect}
                        defaultValue={filterValues.address}
                      />
                      {isStandalonePage && (
                        <>
                          <IconContainer>
                            <UserOutlined />
                          </IconContainer>
                          <MemberSelect
                            allowClear
                            onChange={value => {
                              filtersUpdateSeveral([
                                { member_id: value },
                                { plan_id: undefined },
                                { insurance_plan_tiers: undefined }
                              ])
                              setPlanTiers(null)
                            }}
                            value={filterValues.member_id}
                            fontSize={16}
                          />
                        </>
                      )}
                      <IconContainer>
                        <IdcardOutlined />
                      </IconContainer>
                      <InsuranceAutocomplete
                        memberId={
                          isStandalonePage ? filterValues.member_id : memberId
                        }
                        onChange={value => {
                          filtersUpdateSeveral([
                            { plan_id: value },
                            { insurance_plan_tiers: undefined }
                          ])
                          setPlanTiers(null)
                        }}
                        onPlanSelect={setPlanTiers}
                        defaultValue={filterValues.plan_id}
                        isCarePlan={!isStandalonePage}
                      />
                    </Space.Compact>
                    <FacilitiesFilters
                      filterValues={filterValues}
                      filtersUpdate={filtersUpdate}
                      filtersUpdateSeveral={filtersUpdateSeveral}
                      onResetFilters={onResetFilters}
                      planTiers={planTiers}
                      isStandalonePage={isStandalonePage}
                    />
                  </Space>
                </SearchSection>
                <ResultsSection>
                  <ResultsTable>
                    <FacilitiesTable
                      filterValues={filterValues}
                      isStandalonePage={isStandalonePage}
                      isSelectionMode={
                        isSelectionMode || isCarePlanDraftCreation
                      }
                      addButtonText={
                        isStandalonePage
                          ? 'Add To Care Plan Draft'
                          : addButtonText
                      }
                      onSelectEntity={handleEntitySelect}
                      onRemoveEntity={onRemoveEntity}
                      entitiesIDs={entitiesIDs}
                      setMarkers={setMarkers}
                      setMarkersLoading={setMarkersLoading}
                      employee_id={
                        isStandalonePage ? filterValues.member_id : memberId
                      }
                    />
                  </ResultsTable>

                  {!isCarePlanDraftCreation && (
                    <ResultsMap>
                      {markersLoading ? (
                        <Spin spinning />
                      ) : (
                        <Map
                          markers={markers}
                          leftClickOpens={FACILITIES_PATH}
                          rightClickOpens="google_maps_link"
                        />
                      )}
                    </ResultsMap>
                  )}
                </ResultsSection>
              </PageContainer>

              {isCarePlanDraftCreation && (
                <CarePlanDraftSider insuranceModalState={insuranceModalState} />
              )}
            </Root>
          )
        }}
      </FilterSystem>
      <FacilityCreateModal
        visible={isCreateModalOpen}
        onCancel={onModalClose}
      />
    </div>
  )
}

Facilities.propTypes = {
  onSelectEntity: PropTypes.func,
  onRemoveEntity: PropTypes.func,
  entitiesIDs: PropTypes.array,
  isStandalonePage: PropTypes.bool,
  isSelectionMode: PropTypes.bool,
  addButtonText: PropTypes.string,
  memberId: PropTypes.string
}

export default Facilities
